import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-light-svg-icons';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React from 'react';
import { pageContext, get } from 'ctm-util-functions';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import { MechaButton } from '../../../../ctm-components/components/mecha_components/mecha_button/mecha_button';

const CTAContainer = styled.div`
    bottom: 0px;
    position: ${(props) => (props.isSticky ? 'sticky' : 'unset')};
    width: 100%;
    display: flex;
    justify-content: center;
    background: #ffffff;
    box-shadow: 0px -2px 4px 0px rgba(208, 206, 206, 0.5);
    margin-top: 0px;
    align-items: center;
    flex-direction: column;
`;

const CTAInnerContainer = styled.div`
    max-width: 752px;
    width: 100%;
    display: flex;
    gap: 45px;
    padding: 16px;
    align-items: center;
    @media only screen and (max-width: 600px) {
        flex-direction: column;
        gap: 16px;
        padding-top: 40px;
        padding-right: 16px;
        padding-left: 16px;
    }
`;

const ConfirmationContainer = styled.div`
    display: flex;
    gap: 8px;
    flex-direction: column;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ConfirmationCheckBoxContainer = styled.div``;

const ConfirmationText = styled.label``;

const ScrollToTopContainer = styled.div`
    display: flex;
    padding: 14px 16px;
    justify-content: center;
    align-items: center;
    position: ${(props) => (props.hasSeenFooter ? 'absolute' : 'relative')};
    right: ${(props) => (props.hasSeenFooter ? '32px' : '-190px')};
    top: -30px;
    border-radius: 56px;
    background: #0f58ab;
    box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.12);
    width: 60px;
    height: 60px;
`;

const ErrorMessageContainer = styled.div`
  display: flex;
  padding: 0 2rem;
  background: #FBEDEE;
  align-items: center;
  height: 5.4rem;
  gap: 1rem;
`;

const ErrorMessage = styled.span`
  color: #404756;
  width: fit-content;
  font-size: 1.4rem;
`;

/**
 * Get the ProductCategory state.
 * @param  {Object} state   - The application Redux store.
 * @returns {Object}         - The Product Category state.
 */
const productCategorySelector = (state) => {
    const productCategory = get(pageContext.getProps(), 'verticalSelectors.getProductCategory', false);
    if (productCategory) {
        return productCategory(state);
    }
    return null;
};

const _StickyCTA = ({ state, buttonLabel, enableCTA, setConfirmFlag, handoverUrl, isEditableFlag,
    dispatchGtmEvents, isSticky, hasSeenFooter, isAnswersVeried, hasClickedCheckbox,
    setCheckboxClicked, hasAssumedDriverClaims,  hasAssumedDriverLicenceType,
    hasAssumedYoungerDriverLicenceType, hasAssumedDriverComprehensiveInsured, hasAssumedDriverComprehensiveInsuredYears }) => {
    // Only rendering cta error text and disabling the get quotes button when there are assumed questions for regular or younger driver

    const hasDriverAssumedQuestions = hasAssumedDriverLicenceType || hasAssumedDriverClaims || hasAssumedDriverComprehensiveInsured || hasAssumedDriverComprehensiveInsuredYears;
    const onConfirmedClicked = () => {
        if (hasDriverAssumedQuestions || hasAssumedYoungerDriverLicenceType) {
            setCheckboxClicked(true);
            setConfirmFlag(false);
        } else {
            setCheckboxClicked(true);
            setConfirmFlag(!enableCTA);
        }
    };
    const onGetQuoteClicked = (event) => {
        // We need to fire the Google Tag Analytics events
        // pending whether isEditable is false or not.
        // This only occurs if isEditable is false - ie. the form is in the "Default" state
        // when the user has not attempted to edit anything.
        event.preventDefault();
        if (isEditableFlag) {
            pageContext.getProps().browserHistory.push('/car-insurance/journey/results');
        } else {
            dispatchGtmEvents();
            window.open(handoverUrl, '_self');
        }
    };
    const onScrollToTopClicked = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <CTAContainer isSticky={isSticky}>
            <CTAInnerContainer>
                <ConfirmationContainer onClick={onConfirmedClicked}>
                    <Row>
                        <ConfirmationCheckBoxContainer>
                            {enableCTA ? <FontAwesomeIcon icon={faCheckSquare} color="#0B60B7" size="xl" /> : <FontAwesomeIcon icon={faSquare} color="#DADADA" size="xl" />}
                        </ConfirmationCheckBoxContainer>
                        <ConfirmationText>By continuing, I confirm all of the information is true and correct.</ConfirmationText>
                    </Row>
                    {(hasDriverAssumedQuestions || hasAssumedYoungerDriverLicenceType) && hasClickedCheckbox
                        && (
                            <ErrorMessageContainer>
                                <FontAwesomeIcon icon={faExclamationCircle} color="#B91E28" size="lg" />
                                <ErrorMessage>Please provide all the required information above</ErrorMessage>
                            </ErrorMessageContainer>
                        )}
                </ConfirmationContainer>
                <MechaButton
                    buttonType="hero"
                    buttonLabel={buttonLabel}
                    buttonSize="lg"
                    data-id={'get_quote_confirmation_button'}
                    isDisabled={!enableCTA}
                    onClick={onGetQuoteClicked}
                />
            </CTAInnerContainer>
            <ScrollToTopContainer hasSeenFooter={hasSeenFooter} onClick={onScrollToTopClicked}>
                <FontAwesomeIcon icon={faArrowUp} color="#FFFFFF" size="2xl" />
            </ScrollToTopContainer>

        </CTAContainer>
    );
};

_StickyCTA.propTypes = {
    state: PropTypes.object,
    handoverUrl: PropTypes.string.isRequired,
    buttonLabel: PropTypes.string.isRequired,
    enableCTA: PropTypes.bool.isRequired,
    isEditableFlag: PropTypes.bool.isRequired
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} dispatch - Redux store state.
 * @return {Object} - The state attributes to add to props.
 */

const mapDispatchToProps = (dispatch) => ({
    setCheckboxClicked: (newValue) => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasClickedCheckbox',
            value: newValue
        });
    },
    setConfirmFlag: (newValue) => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: '11MonthsEnableCTA',
            value: newValue
        });
    },
    dispatchGtmEvents: () => {
        dispatch({ type: 'ELEVEN_MONTH_QUOTE_START' });
        dispatch({ type: 'ELEVEN_MONTH_RESULTS' });
    }
});
const mapStateToProps = (state, item) => {
    const enableCTA = state.getIn(['page', '11MonthsEnableCTA']) ?? false;
    const hasClickedCheckbox = state.getIn(['page', 'hasClickedCheckbox']) ?? false;
    const isEditableFlag = state.getIn(['vertical', 'quote', 'form', 'isEditable']) ?? false;
    const providerName = state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'providerMeta', 'name']);
    const handoverUrl = state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'handoverUrl']);
    const buttonLabel = isEditableFlag ? 'Get quotes' : `Go to ${providerName}`;

    return {
        state,
        enableCTA,
        isEditableFlag,
        handoverUrl,
        buttonLabel,
        hasClickedCheckbox,
        hasAssumedDriverClaims: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverClaims']),
        hasAssumedDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverLicenceType']),
        hasAssumedYoungerDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedYoungerDriverLicenceType']),
        hasAssumedDriverComprehensiveInsured: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsured']),
        hasAssumedDriverComprehensiveInsuredYears: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsuredYears'])
    };
};

const StickyCTA = connect(
    mapStateToProps,
    mapDispatchToProps
)(_StickyCTA);

export { _StickyCTA, StickyCTA };
